/**
 * The three layers, named. A1 in `docs/plans/2026-08-28-wave-1-atoms.md`.
 *
 * The word "verdict" had come to mean three different things, and the code's
 * `Verdict` held an INSTRUMENT's output while being named after a DERIVATION.
 * Proposal section 4 separates them:
 *
 *   Instrument  -> an Observation.  Tier 0 reproducible, Tier 1 not, and that is fine.
 *   Assessment  -> a judgement about one feed in context. Not reproducible.
 *   Decision    -> what airs. Reproducible, and that is where recomputability lives.
 *
 * Collapsing the middle layer is what made the old shape brittle: a picture
 * reviewer said `approved: false` and the feed was out, with no room for
 * "the shake spiked for two seconds after a scoring play".
 */
import type { WindowId } from './window.js';
import type { EscalationReason } from './cascade.js';

/**
 * A reason is a type, not a sentence.
 *
 * Proposal section 7.2: a rejection must carry the rubric line it was rejected
 * under, the model that ruled, and a route to a human, because a rejection must
 * be able to be wrong out loud. Bare strings cannot carry any of that, and every
 * consumer ends up reformatting them differently.
 *
 * Note the asymmetry with `Ruling` in the review path, which keeps
 * `reasons: string[]`: that is what a MODEL returns and `npm run test:live`
 * guards its shape. This is what the SYSTEM records. The conversion happens once,
 * in `server/review/client.ts`, where the rubric version is known.
 */
export interface Reason {
  /** Stable, greppable, survives rewording of the prose. */
  code: string;
  /** Verbatim from editorial.ts where one applies, never paraphrased. */
  rubricLine: string;
  /**
   * `RUBRIC_VERSION` at the time of the ruling, so rule changes are visible.
   *
   * **Only values that RESOLVE against the published version history**, which
   * `SITE10` enforces. Three call sites pushed a decision-rule name through this
   * field instead (`editorial_extremum@1`, `feed_verdict@1`, `luma@1`), so a
   * publisher held by a derived rule was shown `luma@1`, opened the version
   * history, and found `v1.0.0` and `v1.1.0`. The audit trail the published
   * standard exists to close had a dead link in it for a whole class of rulings.
   */
  rubricVersion: string;
  /**
   * The channel whose clauses were composed into the rubric, and the version
   * they came from. **Both present or both absent.**
   *
   * `rubricVersion` alone is the FLOOR's version, and for months that was the
   * whole citation. A channel clause code reaches a surface through
   * `clauseOf(code, extra)`, which searches the floor AND any channel clauses
   * supplied, so a publisher refused under a channel's clause was handed
   * `v1.1.0`, **which does not contain the rule they were refused under**. Not
   * an incomplete citation: one that resolves, to the wrong document, and looks
   * correct. Measured 2026-09-07.
   *
   * This is the second time this field has had that defect. `rubricVersion`'s
   * own note above records the first, where three call sites pushed a decision
   * rule through it and a publisher opened the version history to find nothing.
   * That was fixed for derived rules and left open for channels.
   *
   * **Absent is the ordinary case and is not a gap.** `composeRules` returns
   * `under: null` when the composition is byte-identical to the floor, so a
   * ruling made under the floor alone says so by carrying nothing. The local
   * triage tier composes no channel clauses at all (issue 027), so most rulings
   * on a channel node will correctly carry no channel here, which is what makes
   * the size of that issue visible on a publisher-facing surface for the first
   * time.
   *
   * `channelVersion` is what `channel_versions` is keyed by and resolves there
   * forever, which is the property migration `0004` was written to provide and
   * which nothing consumed until now.
   */
  channel?: string;
  channelVersion?: string;
  /**
   * The named derivation that produced this ruling, where one did.
   *
   * Two fields were doing one field's work, and separating them at the type
   * level is the fix, because convention is what produced the collision. A
   * derived ruling carries BOTH: it was made by that rule, under that clause
   * set. Absent on a model ruling, which has no decision rule.
   */
  decisionRule?: string;
  /** Which model or function said so. */
  instrument: string;
  /** Where a human disagrees with it. Absent means no appeal path exists yet. */
  appeal?: string;
}

/** Free-form text is still allowed, but it arrives as a typed reason. */
export function reason(
  code: string,
  rubricLine: string,
  rubricVersion: string,
  instrument: string,
  appeal?: string,
  under?: { channel: string; version: string } | null,
): Reason {
  const cite = under ? { channel: under.channel, channelVersion: under.version } : {};
  return appeal === undefined
    ? { code, rubricLine, rubricVersion, instrument, ...cite }
    : { code, rubricLine, rubricVersion, instrument, appeal, ...cite };
}

/**
 * A reason citing a rule that is in no published document.
 *
 * A code that is not a floor clause and that names no channel resolves to
 * nothing: the reader opens `rubricVersion` and the rule is not there. That is
 * the defect this field was added for, and it comes back the moment somebody
 * builds a `Reason` from a channel-composed ruling without threading the
 * composition through.
 *
 * **Deliberately not the inverse of "is attributable".** Written as a positive
 * predicate with its own name and its own guard, because collapsing it into a
 * `!` at three call sites is the single edit that makes it disappear while every
 * type still checks, which is the shape `ingestRefuses` already exists to avoid.
 *
 * `<gate>.finding` is exempt and is not a citation: it is the honest marker for
 * a model's own sentence, which names no clause and never claimed to. Filling
 * `code` from a model's guess would be worse than carrying what it said.
 */
export function unattributable(r: Reason, floor: ReadonlySet<string>): boolean {
  if (r.code.endsWith('.finding')) return false;
  if (floor.has(r.code)) return false;
  return r.channel === undefined;
}

/** For the places that only have a display slot. Never for storage. */
export function reasonText(r: Reason): string {
  return r.rubricLine;
}

/**
 * What an instrument reports about one sample of one feed. Never an authority
 * on its own.
 *
 * Observations expire. A ruling on what the camera showed 40 seconds ago does
 * not authorise what it is showing now, so a stale observation is treated
 * exactly like no observation at all: not eligible.
 */
export interface Observation {
  reviewer: 'visual' | 'speech';
  approved: boolean;
  /** When the reviewed material was captured, not when the ruling returned. */
  sampledAt: number;
  decidedAt: number;
  /** Why it was rejected. Empty when approved. */
  reasons: Reason[];
  /** Rubric version the reviewer was working from, so rule changes invalidate. */
  rubric: string;
  /**
   * Which reviewer actually ruled. A local filter's approval is provisional and
   * expires faster; only the remote judge is authoritative.
   */
  decidedBy: 'local' | 'remote';
  /**
   * True when the frontier judge ruled on the cheap tier's DESCRIPTION rather
   * than on the frames.
   *
   * Weaker evidence for the same reason a local approval is: it inherits the
   * describer's blind spots, and the clauses a describer would plausibly stay
   * silent about are the four operator-disclosure ones no fixture can test. So
   * it expires on the LOCAL clock, not the remote one, and buys one cycle rather
   * than standing consent. That is the same rule and the same reason as
   * `localTtlMs`, applied to a second kind of cheap verdict.
   *
   * Without this a feed judged on words while it could not win the window would
   * still hold that verdict six windows later, when it can.
   */
  fromDescription?: true;
  /** Why the cascade escalated, when it did. Recorded for the session log. */
  escalation?: EscalationReason;
  /** Set when the reviewer itself failed. A failed review is never an approval. */
  error?: string;
  /**
   * What the reviewer says it saw or heard, in plain words. Present when the
   * two-stage filter ran. This is what an operator is actually owed: "we saw a
   * completely black image" is feedback, "camera not showing the event" is only
   * a ruling.
   */
  observed?: string;
}

/**
 * The middle layer. A judgement about ONE feed, in context, by the coach that
 * is attached to it. Not reproducible, and deliberately not an authority: an
 * assessment may never excuse a content finding, because a coach that could talk
 * a speech rejection down would be a model deciding what airs, one feed at a
 * time (proposal sections 11.3 and 10).
 *
 * Unused until wave 2. Declared here so the layer exists before something needs
 * it and puts it in the wrong place.
 */
export interface Assessment {
  feedId: string;
  windowId: WindowId;
  /** What the coach concluded, in plain words, for the publisher. */
  note: string;
  /** Apparatus observations only. Never a content ruling. */
  about: 'transport' | 'picture' | 'sound' | 'framing';
  /** True when the coach is asking the operator to hold rather than act. */
  hold: boolean;
  at: number;
}

/**
 * Where every offered feed lands. Proposal section 7.1: the fates must sum to
 * the feeds offered, and a feed that vanishes between offer and decision is a
 * bug the count catches.
 */
export type Fate =
  | 'selected'
  /**
   * The picture was not chosen and THIS FEED'S SOUND IS ON AIR.
   *
   * Added 2026-08-31 by a code review of the picture-and-sound split. Before it,
   * a publisher whose audio was being broadcast under somebody else's picture
   * was told `eligible_not_selected`, which is false: they were selected, for
   * the sound. The fate record is this project's appeal artifact, so a
   * contribution it cannot express is a contribution the publisher cannot see
   * and cannot appeal.
   *
   * A separate value rather than `selected`, because the two are different
   * facts: one publisher's picture is on air and another's sound is, and a
   * record that called both `selected` could not say whose camera you are
   * watching.
   */
  | 'selected_for_sound'
  | 'eligible_not_selected'
  | 'rejected_by_gate'
  | 'excluded_unaligned'
  | 'excluded_contaminated'
  | 'stale';

/** A derivation over observations, for one feed at one window. Reproducible. */
export interface FeedVerdict {
  feedId: string;
  windowId: WindowId;
  eligible: boolean;
  /**
   * May this feed supply the PICTURE, whatever its sound did?
   *
   * Stage 2. `eligible` above is the conjunction and is what decides whether a
   * feed goes to air whole; this is what decides whether it goes to air at all.
   * A feed with `pictureEligible: true` and `eligible: false` is broadcast
   * SILENT rather than replaced by the slate.
   *
   * Required rather than optional, for the reason `OnAir.silent` is: an omitted
   * answer would default to something, and every default here is a decision
   * about what reaches an audience.
   */
  pictureEligible: boolean;
  fate: Fate;
  perGate: Record<string, { passed: boolean; reason: Reason | null }>;
  /** e.g. 'feed_verdict@1'. A verdict that does not name its rule is untraceable. */
  rule: string;
  /** Observation ids, so the derivation can be replayed rather than trusted. */
  computedFrom: string[];
}

/**
 * An editor's good/bad judgment, and the one boolean that keeps it meaningful.
 *
 * Proposal section 11.6. This is the only gold label there is, and the only
 * thing the local filter's miss rate can honestly be measured against. If the
 * labels become agent-proposed and human-accepted, the filter is tuned on the
 * model's own opinions laundered through a person: agreement rises and tells you
 * nothing, improvement and convergence become indistinguishable, and nothing
 * announces it. Only labels a human produced unprompted enter the evaluation
 * set, which is impossible to reconstruct later if the flag was never recorded.
 */
export interface OverruleAudit {
  recorded: number;
  resolved: number;
  correct: number;
  /** Null until anything has resolved. Absence of a rate is not a good rate. */
  accuracy: number | null;
  pending: number;
  /** False until the coach has earned it, which is the point of measuring. */
  maySuppress: boolean;
}

/**
 * How often the editor is agreeing with the machine. Proposal section 11.5.
 *
 * Lives beside `Label` because it is a statement about labels, and in `shared/`
 * because the director renders it: a type the server owns and the client draws
 * belongs to neither of them alone.
 */
export interface AcceptanceAudit {
  total: number;
  /** Labels that agreed with a proposal the editor could already see. */
  suggested: number;
  /** Labels an editor produced unprompted. The only ones that may enter an evaluation set. */
  unprompted: number;
  /** Null with no labels yet, and that is NOT zero. */
  rate: number | null;
  /** True only once there is enough of a sample to be uncomfortable about. */
  concerning: boolean;
}

export interface Label {
  feedId: string;
  windowId: WindowId;
  good: boolean;
  /** True when an agent proposed this label before the human agreed with it. */
  suggested: boolean;
  by: string;
  at: number;
}
